// @ts-ignore — JS constants file, resolved by Vite
import { FRUSTUM_LIGHT } from '@/constants/object';
import * as THREE from 'three';
import type { FrustumLightOptions } from './projector';

/**
 * FrustumLight — semi-transparent side faces for a projector frustum volume.
 *
 * Creates 4 triangular faces (top, bottom, left, right) forming the
 * pyramid between the projector origin and the target rectangle.
 * The front face is intentionally omitted.
 *
 * Usage:
 *   const light = new FrustumLight(gizmoScene, gizmoLayer, { color, opacity });
 *   light.update(origin, frameCorners);   // call each frame
 *   light.setColor(color);
 *   light.setVisible(bool);
 *   light.dispose();
 */
export class FrustumLight {
  private readonly positions: Float32Array;
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly mesh: THREE.Mesh;
  private readonly parentScene: THREE.Scene;

  constructor(parentScene: THREE.Scene, layer: number, options: FrustumLightOptions = {}) {
    const color = options.color ?? FRUSTUM_LIGHT.COLOR;
    const opacity = options.opacity ?? FRUSTUM_LIGHT.OPACITY;

    // 4 triangles * 3 vertices * 3 components
    const floatCount = FRUSTUM_LIGHT.TRIANGLES * 3 * 3;
    this.positions = new Float32Array(floatCount);
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));

    this.material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      side: THREE.DoubleSide,
      depthWrite: FRUSTUM_LIGHT.DEPTH_WRITE,
      depthTest: FRUSTUM_LIGHT.DEPTH_TEST,
    });

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = FRUSTUM_LIGHT.RENDER_ORDER;
    this.mesh.layers.set(layer);
    this.mesh.userData.projectorOverlayIgnore = true;

    this.parentScene = parentScene;
    parentScene.add(this.mesh);
  }

  /**
   * Update the frustum face positions.
   * @param origin — projector world position (pyramid apex)
   * @param corners — 4 frame corners at target distance
   *   [0]=bottom-left, [1]=bottom-right, [2]=top-right, [3]=top-left
   */
  update(origin: THREE.Vector3, corners: THREE.Vector3[]): void {
    if (!corners || corners.length < FRUSTUM_LIGHT.TRIANGLES) return;
    const p = this.positions;
    for (let t = 0; t < FRUSTUM_LIGHT.TRIANGLES; t++) {
      const c0 = corners[t];
      const c1 = corners[(t + 1) % corners.length];
      const off = t * 9;
      p[off] = origin.x;
      p[off + 1] = origin.y;
      p[off + 2] = origin.z;
      p[off + 3] = c0.x;
      p[off + 4] = c0.y;
      p[off + 5] = c0.z;
      p[off + 6] = c1.x;
      p[off + 7] = c1.y;
      p[off + 8] = c1.z;
    }
    this.geometry.attributes.position.needsUpdate = true;
  }

  setColor(color: THREE.ColorRepresentation): void {
    this.material.color.set(color);
  }

  setOpacity(opacity: number): void {
    this.material.opacity = opacity ?? FRUSTUM_LIGHT.OPACITY;
  }

  setVisible(visible: boolean): void {
    this.mesh.visible = visible;
  }

  dispose(): void {
    this.parentScene.remove(this.mesh);
    this.geometry.dispose();
    this.material.dispose();
  }
}
